import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Button, Col, Divider, Flex, Input, InputNumber, Modal, Row, Select, message } from "antd"
import { CloseOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { serialize } from "object-to-formdata"
import { getAddOnCategories, createAddOnCategory } from "store/trips"
import { getTrips } from "store/boats"
import { Text, Title } from "ui/Typography"
import ImageCropper from "ui/ImageCropper"
import { MAX_MEDIA_SIZE } from "constants/form"
import { colors, spacings } from "themes/variables"
import styles from "./AddOnModal.module.css"

const { TextArea } = Input

const renderExperienceOption = (t) => (option) => {
  const status = String(option?.data?.status || "").toLowerCase()
  return (
    <Flex align="center" gap={spacings.XS}>
      <Text>{option?.data?.label}</Text>
      {status === "published" && (
        <Text color={colors.success}>({t("settings.add_ons.status_published", { defaultValue: "Published" })})</Text>
      )}
      {status === "draft" && (
        <Text color={colors.textSecondary}>({t("settings.add_ons.status_draft", { defaultValue: "Draft" })})</Text>
      )}
    </Flex>
  )
}

const buildAddOnFormData = ({ title, category, price, minPerOrder, maxPerOrder, description, experienceIds, photoFile }) => {
  const attrs = {
    title,
    description,
    price,
    add_on_category_id: category,
    ...(minPerOrder == null ? {} : { min_per_order: minPerOrder }),
    ...(maxPerOrder == null ? {} : { max_per_order: maxPerOrder }),
    trip_ids: experienceIds?.length ? experienceIds : []
  }
  const formData = serialize({ add_on: attrs }, { allowEmptyArrays: true, indices: true })
  if (photoFile instanceof File) formData.append("add_on[photo]", photoFile)
  return formData
}

const AddOnModal = ({ visible, onCancel, onSave, isSaving, initialValues }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const addOnCategories = useSelector((state) => state.trips.addOnCategories.data)
  const isCategoriesLoading = useSelector((state) => state.trips.addOnCategories.loading)
  const experiencesList = useSelector((state) => state.trips?.items || [])
  const experiencesLoading = useSelector((state) => state.trips?.loading || false)

  const [newCategoryName, setNewCategoryName] = useState("")
  const newCategoryInputRef = useRef(null)
  const fileInputRef = useRef(null)

  const [photo, setPhoto] = useState(null)
  const [photoFile, setPhotoFile] = useState(null)
  const [cropSrc, setCropSrc] = useState(null)
  const [isCropVisible, setIsCropVisible] = useState(false)
  const [pendingFile, setPendingFile] = useState(null)

  const [category, setCategory] = useState(null)
  const [title, setTitle] = useState("")
  const [price, setPrice] = useState(null)
  const [minPerOrder, setMinPerOrder] = useState(null)
  const [maxPerOrder, setMaxPerOrder] = useState(null)
  const [description, setDescription] = useState("")
  const [experienceIds, setExperienceIds] = useState([])

  useEffect(() => {
    if (visible) {
      dispatch(getAddOnCategories())
      dispatch(getTrips())
    }
  }, [dispatch, visible])

  useEffect(() => {
    if (visible && initialValues) {
      setPhoto(initialValues.photo_url ?? initialValues.photo ?? null)
      setPhotoFile(null)
      setCategory(initialValues.add_on_category?.id ?? initialValues.category ?? null)
      setTitle(initialValues.title ?? "")
      setPrice(initialValues.price == null ? null : Number(initialValues.price))
      setMinPerOrder(initialValues.min_per_order ?? null)
      setMaxPerOrder(initialValues.max_per_order ?? null)
      setDescription(initialValues.description ?? "")
      setExperienceIds(initialValues.trip_ids ?? initialValues.associated_trips?.map((tr) => tr.id) ?? [])
    } else if (visible && !initialValues) {
      setPhoto(null)
      setPhotoFile(null)
      setCategory(null)
      setTitle("")
      setPrice(null)
      setMinPerOrder(null)
      setMaxPerOrder(null)
      setDescription("")
      setExperienceIds([])
    }
  }, [visible, initialValues])

  const categoryOptions = addOnCategories.map((c) => ({ value: c.id, label: c.name }))
  const experienceOptions = (() => {
    const base = (experiencesList || []).map((e) => ({
      value: e.id,
      label: e.title || e.name,
      status: e.status
    }))
    const known = new Set(base.map((o) => o.value))
    const assigned = (initialValues?.associated_trips || [])
      .filter((tr) => !known.has(tr.id))
      .map((tr) => ({ value: tr.id, label: tr.title || tr.name, status: tr.status }))
    return [...base, ...assigned]
  })()

  const priceError = price != null && price < 0 ? t("experiences.add_ons.price_error") : null
  const minPerOrderError = minPerOrder != null && minPerOrder <= 0 ? t("experiences.add_ons.min_error") : null
  const getMaxPerOrderError = () => {
    if (maxPerOrder != null && maxPerOrder <= 0) return t("experiences.add_ons.max_error")
    if (maxPerOrder != null && minPerOrder != null && maxPerOrder < minPerOrder) return t("experiences.add_ons.max_min_error")
    return null
  }
  const maxPerOrderError = getMaxPerOrderError()

  const canSave =
    !!category &&
    title.trim() !== "" &&
    price != null &&
    description.trim() !== "" &&
    !priceError &&
    !minPerOrderError &&
    !maxPerOrderError

  const handleAddCategory = async () => {
    const name = newCategoryName.trim()
    if (!name) return
    await dispatch(createAddOnCategory(name))
    setNewCategoryName("")
    requestAnimationFrame(() => newCategoryInputRef.current?.focus())
  }

  const closeCropper = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc(null)
    setPendingFile(null)
    setIsCropVisible(false)
  }

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    if (file.size / 1024 / 1024 > MAX_MEDIA_SIZE.image) {
      message.error(t("media_upload.messages.image_size_limit_exceeded", { maxSize: MAX_MEDIA_SIZE.image }))
      return
    }
    setCropSrc(URL.createObjectURL(file))
    setPendingFile(file)
    setIsCropVisible(true)
  }

  const handleCropComplete = (croppedBlob) => {
    if (!croppedBlob || !pendingFile) { closeCropper(); return }
    const base = (pendingFile.name || "image").replace(/\.[^/.]+$/, "")
    const croppedFile = new File([croppedBlob], `${base}-cropped.jpg`, { type: "image/jpeg" })
    setPhoto(URL.createObjectURL(croppedBlob))
    setPhotoFile(croppedFile)
    closeCropper()
  }

  const handleRemovePhoto = (e) => {
    e.stopPropagation()
    setPhoto(null)
    setPhotoFile(null)
  }

  const handleSave = async () => {
    if (!canSave) return
    const formData = buildAddOnFormData({ title, category, price, minPerOrder, maxPerOrder, description, experienceIds, photoFile })
    await onSave?.(formData)
  }

  return (
    <Modal open={visible} onCancel={onCancel} footer={null} width={560} centered destroyOnClose maskClosable={false}>
      <Title level={4} className="mb-m">
        {initialValues ? t("settings.edit_add_on") : t("settings.add_new_add_on")}
      </Title>
      <input ref={fileInputRef} type="file" accept="image/*" className={styles.hiddenInput} onChange={handlePhotoChange} />

      <div className="mb-m">
        {photo ? (
          <div className={styles.photoPreview}>
            <img src={photo} alt="addon" className={styles.photoImg} />
            <button className={styles.removeBtn} onClick={handleRemovePhoto} type="button">
              <CloseOutlined />
            </button>
          </div>
        ) : (
          <button type="button" className={styles.uploadCard} onClick={() => fileInputRef.current?.click()}>
            <UploadOutlined className={styles.uploadIcon} />
            <Text className={styles.uploadLabel}>{t("experiences.add_ons.upload")}</Text>
            <Text className={styles.uploadHint}>{t("experiences.add_ons.upload_hint")}</Text>
          </button>
        )}
      </div>

      <div className="mb-m">
        <label className={styles.label}>{t("experiences.add_ons.category_label")}</label>
        <Select
          value={category}
          onChange={setCategory}
          placeholder={t("experiences.add_ons.category_placeholder")}
          options={categoryOptions}
          loading={isCategoriesLoading}
          showSearch
          filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          size="large"
          className={styles.fullWidth}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider className={styles.dropdownDivider} />
              <Flex gap={8} className="px-s pb-s">
                <Input
                  ref={newCategoryInputRef}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddCategory() } }}
                  placeholder={t("experiences.add_ons.new_category_placeholder")}
                  size="small"
                />
                <Button
                  type="primary"
                  variant="outlined"
                  icon={<PlusOutlined />}
                  onClick={handleAddCategory}
                  disabled={!newCategoryName.trim() || isCategoriesLoading}
                  loading={isCategoriesLoading}
                >
                  {t("experiences.add_ons.add")}
                </Button>
              </Flex>
            </>
          )}
        />
      </div>

      <div className="mb-m">
        <label className={styles.label}>{t("experiences.add_ons.title_label")}</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("experiences.add_ons.title_placeholder")} size="large" />
      </div>

      <Row gutter={16} className="mb-m">
        <Col span={8}>
          <label className={styles.label}>{t("experiences.add_ons.price_label")}</label>
          <InputNumber
            value={price}
            onChange={setPrice}
            prefix="$"
            placeholder={t("experiences.add_ons.price_placeholder")}
            min={0}
            size="large"
            className={styles.fullWidth}
            status={priceError ? "error" : undefined}
          />
          {priceError && <div className={styles.fieldError}>{priceError}</div>}
        </Col>
        <Col span={8}>
          <label className={styles.label}>{t("experiences.add_ons.min_per_order")}</label>
          <InputNumber
            value={minPerOrder}
            onChange={setMinPerOrder}
            placeholder={t("experiences.add_ons.min_placeholder")}
            min={0}
            size="large"
            className={styles.fullWidth}
            status={minPerOrderError ? "error" : undefined}
          />
          {minPerOrderError && <div className={styles.fieldError}>{minPerOrderError}</div>}
        </Col>
        <Col span={8}>
          <label className={styles.label}>{t("experiences.add_ons.max_per_order")}</label>
          <InputNumber
            value={maxPerOrder}
            onChange={setMaxPerOrder}
            placeholder={t("experiences.add_ons.max_placeholder")}
            min={0}
            size="large"
            className={styles.fullWidth}
            status={maxPerOrderError ? "error" : undefined}
          />
          {maxPerOrderError && <div className={styles.fieldError}>{maxPerOrderError}</div>}
        </Col>
      </Row>

      <div className="mb-m">
        <label className={styles.label}>
          {t("settings.add_ons.experiences_label")}
        </label>
        <Select
          mode="multiple"
          value={experienceIds}
          onChange={setExperienceIds}
          placeholder={t("settings.add_ons.experiences_placeholder")}
          options={experienceOptions}
          optionRender={renderExperienceOption(t)}
          loading={experiencesLoading}
          showSearch
          filterOption={(input, option) => (option.label || "").toLowerCase().includes(input.toLowerCase())}
          size="large"
          className={styles.fullWidth}
          maxTagCount="responsive"
        />
      </div>

      <div className="mb-xl">
        <label className={styles.label}>{t("experiences.add_ons.description_label")}</label>
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("experiences.add_ons.description_placeholder")}
          rows={4}
          size="large"
        />
      </div>

      <Flex justify="end" gap={spacings.S}>
        <Button onClick={onCancel} color="primary" variant="outlined" disabled={isSaving}>
          {t("experiences.add_ons.cancel")}
        </Button>
        <Button onClick={handleSave} disabled={!canSave} loading={isSaving} type="primary" color="primary" variant="solid">
          {t("experiences.add_ons.save")}
        </Button>
      </Flex>

      <ImageCropper
        visible={isCropVisible}
        onClose={closeCropper}
        imageSrc={cropSrc || ""}
        onCropComplete={handleCropComplete}
        lockSize={false}
      />
    </Modal>
  )
}

AddOnModal.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  initialValues: PropTypes.object
}

export default AddOnModal
